import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import SearchableSelect from '../../shared/SearchableSelect';
import Pagination from '../components/Pagination';

import {
  getIssueRenewalsByLibrary,
  getIssuesByLibrary,
  addIssueRenewal,
  getErrorMessage
} from '../../api/api';
import '../lib-page-header.css';

const PAGE_SIZE = 10;

const RenewalList = ({ embedded = false, actionsPortalNode = null } = {}) => {
  const { user } = useAuth();

  const libraryId = useActiveLibraryId();
  const staffId = user?.id || user?.staff_id;

  const [renewals, setRenewals] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [formData, setFormData] = useState({
    issue_id: '',
    old_due_date: '',
    new_due_date: '',
    remarks: ''
  });

  const fetchData = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);

      const [renewalData, issueData] = await Promise.all([
        getIssueRenewalsByLibrary(libraryId),
        getIssuesByLibrary(libraryId)
      ]);

      setRenewals(renewalData || []);
      setIssues((issueData || []).filter((item) => item.issue_status === 'issued'));
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'issue_id') {
      const selectedIssue = issues.find((item) => String(item.id) === String(value));

      setFormData((prev) => ({
        ...prev,
        issue_id: value,
        old_due_date: selectedIssue?.due_date
          ? String(selectedIssue.due_date).slice(0, 10)
          : ''
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      issue_id: '',
      old_due_date: '',
      new_due_date: '',
      remarks: ''
    });
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.issue_id || !formData.new_due_date) {
      Swal.fire('Error', 'Issue and new due date are required', 'error');
      return;
    }

    const payload = {
      issue_id: formData.issue_id,
      old_due_date: formData.old_due_date,
      new_due_date: formData.new_due_date,
      renewed_by_staff_id: staffId,
      remarks: formData.remarks || null
    };

    try {
      setSaving(true);

      await addIssueRenewal(payload);

      Swal.fire('Success', 'Book renewed successfully', 'success');
      closeModal();
      fetchData();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const filteredRenewals = renewals.filter((item) => {
    const keyword = search.toLowerCase();
    const member =
      item.member_name ||
      `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
      item.parent_name ||
      '';

    return (
      String(member).toLowerCase().includes(keyword) ||
      String(item.title || item.book_title || '').toLowerCase().includes(keyword) ||
      String(item.remarks || '').toLowerCase().includes(keyword)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredRenewals.length / PAGE_SIZE));
  const paginatedRenewals = filteredRenewals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="container-fluid mt-3">
      <style>
        {`
          .rn-modal-overlay {
            position: fixed; inset: 0; background: rgba(15, 23, 42, 0.55);
            z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 20px;
          }
          .rn-modal-box {
            width: 100%; max-width: 640px; max-height: 92vh; margin: auto;
            display: flex; flex-direction: column; background: #fff;
            border-radius: 14px; overflow: hidden; box-shadow: 0 30px 80px rgba(15, 23, 42, 0.35);
          }
          .rn-modal-head {
            padding: 14px 18px; background: linear-gradient(135deg, #4f46e5, #2563eb);
            color: #fff; display: flex; align-items: center; justify-content: space-between;
          }
          .rn-modal-head h5 { margin: 0; font-weight: 800; font-size: 17px; }
          .rn-modal-close {
            border: 0; background: rgba(255, 255, 255, 0.18); color: #fff;
            width: 32px; height: 32px; border-radius: 50%; font-size: 20px; line-height: 1;
          }
          /* <form> sits between .rn-modal-box (flex column) and the head/
             body/footer sections — without this it's a plain block box, so
             the body never becomes a real flex item and can't scroll. */
          .rn-modal-box form { display: contents; }
          .rn-modal-body { padding: 18px; flex: 1 1 auto; min-height: 0; overflow-y: auto; }
          .rn-modal-body .row { --bs-gutter-x: 14px; }
          .rn-modal-body .mb-3 { margin-bottom: 14px !important; }
          .rn-modal-body .form-label { display: block; height: 18px; margin-bottom: 5px; font-size: 12.5px; }
          .rn-modal-body .form-control,
          .rn-modal-body .form-select { width: 100%; box-sizing: border-box; min-height: 40px; }
          .rn-modal-body .row > .col-md-6 { flex: 0 0 50%; max-width: 50%; }
          .rn-modal-body .row > .col-md-12 { flex: 0 0 100%; max-width: 100%; }
          .rn-modal-footer {
            padding: 14px 18px; border-top: 1px solid #eef2f7;
            display: flex; justify-content: flex-end; gap: 10px;
          }
          @media (max-width: 575px) {
            .rn-modal-box { max-width: 96vw; }
            .rn-modal-body .row > .col-md-6 { flex: 0 0 100%; max-width: 100%; }
          }
        `}
      </style>

      {!embedded && (
        <div className="lib-page-header">
          <div className="lib-page-header__left">
            <div className="lib-page-header__icon">
              <i className="fas fa-sync-alt" />
            </div>
            <div>
              <h3 className="lib-page-header__title">Book Renewals</h3>
              <div className="lib-page-header__subtitle">Renew issued books and extend due date</div>
            </div>
          </div>
          <div className="lib-page-header__actions">
            <button className="btn btn-primary" onClick={openModal}>
              <i className="fas fa-sync-alt me-2" />Create Renewal
            </button>
          </div>
        </div>
      )}

      {embedded && actionsPortalNode && createPortal(
        <button className="btn btn-primary" onClick={openModal}>
          <i className="fas fa-sync-alt me-2" />Create Renewal
        </button>,
        actionsPortalNode
      )}

      {showModal && (
        <div className="rn-modal-overlay">
          <div className="rn-modal-box">
            <form onSubmit={handleSubmit}>
              <div className="rn-modal-head">
                <h5><i className="fas fa-sync-alt me-2" />Create Renewal</h5>
                <button type="button" className="rn-modal-close" onClick={closeModal} disabled={saving}>
                  ×
                </button>
              </div>

              <div className="rn-modal-body">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Issued Book *</label>
                    <SearchableSelect
                      name="issue_id"
                      value={formData.issue_id}
                      onChange={handleChange}
                      options={issues.map((item) => ({
                        value: item.id,
                        label: `${item.title || item.book_title || 'Book'} - ${item.member_name || item.parent_name || 'Member'} - Due: ${item.due_date ? String(item.due_date).slice(0, 10) : '-'}`
                      }))}
                      placeholder="Select Issued Book"
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Old Due Date</label>
                    <input
                      type="date"
                      name="old_due_date"
                      className="form-control"
                      value={formData.old_due_date}
                      onChange={handleChange}
                      disabled
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">New Due Date *</label>
                    <input
                      type="date"
                      name="new_due_date"
                      className="form-control"
                      value={formData.new_due_date}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Remarks</label>
                    <input
                      type="text"
                      name="remarks"
                      className="form-control"
                      value={formData.remarks}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="rn-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Renew'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card shadow-sm mb-3 table-full-card">
        <div className="card-header fw-bold"><i className="fas fa-list me-2" />Renewal List</div>

        <div className="card-body">
          <div className="row g-2 align-items-center mb-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search member, book, remarks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <button type="button" className="btn btn-outline-secondary w-100" onClick={() => setSearch('')}>
                Reset
              </button>
            </div>
          </div>

          {showLoader ? (
            <Loader />
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Member</th>
                    <th>Book</th>
                    <th>Old Due Date</th>
                    <th>New Due Date</th>
                    <th>Renewed On</th>
                    <th>Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedRenewals.length > 0 ? (
                    paginatedRenewals.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                        <td>
                          {item.member_name ||
                            `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
                            item.parent_name ||
                            '-'}
                        </td>
                        <td>{item.title || item.book_title || '-'}</td>
                        <td>{item.old_due_date ? String(item.old_due_date).slice(0, 10) : '-'}</td>
                        <td>{item.new_due_date ? String(item.new_due_date).slice(0, 10) : '-'}</td>
                        <td>{item.created_at ? String(item.created_at).slice(0, 10) : '-'}</td>
                        <td>{item.remarks || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No renewals found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredRenewals.length}
                itemLabel="renewals"
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RenewalList;